import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { Module } from '../interfaces/module.interface';
import { Page } from '../interfaces/page.interface';
import { ModulesService } from '../interfaces/modules.service';
import { SIDEBAR_PAGE_MAPPING } from '../constants/sidebar-module-mapping';

export interface BreadcrumbItem {
  label: string;
  link: string;
}

export interface BreadcrumbFromRouteResult {
  title: string;
  breadcrumb: BreadcrumbItem[];
}

@Injectable({ providedIn: 'root' })
export class ModulesMenuService {
  private normalizeInputPath(pathOrSegment: string): string {
    return ('/' + pathOrSegment.trim().replace(/^\/+/, '').toLowerCase()).replace(/\/+$/, '');
  }

  private isRoutePathMatch(mappingPath: string, normalizedInput: string): boolean {
    const pathNorm = (mappingPath.startsWith('/') ? mappingPath : '/' + mappingPath).toLowerCase().replace(/\/+$/, '');
    return (
      pathNorm === normalizedInput ||
      pathNorm.startsWith(normalizedInput + '/') ||
      normalizedInput.startsWith(pathNorm + '/')
    );
  }

  /**
   * Returns action permission keys for pages mapped to a route segment or full route path.
   * actionCode examples: View, Create, Update, Delete.
   */
  getPermissionKeysForPathAndAction(pathOrSegment: string, actionCode: string): string[] {
    if (!pathOrSegment?.trim() || !actionCode?.trim()) return [];
    const normalizedInput = this.normalizeInputPath(pathOrSegment);
    const normalizedAction = actionCode.toLowerCase();
    const modules = this.modulesSubject.getValue() ?? [];
    const keysSet = new Set<string>();
    for (const mod of modules) {
      if (!mod.pages) continue;
      for (const page of mod.pages) {
        if (!page.isVisible || !page.actionList?.length) continue;
        const mapping = SIDEBAR_PAGE_MAPPING[page.resourceName];
        if (!mapping?.path) continue;
        if (!this.isRoutePathMatch(mapping.path, normalizedInput)) continue;
        const action = page.actionList.find(a => (a.code ?? '').toLowerCase() === normalizedAction);
        if (action?.permissionKey) keysSet.add(action.permissionKey);
      }
    }
    return Array.from(keysSet);
  }

  private modulesSubject = new BehaviorSubject<Module[]>([]);
  modules$: Observable<Module[]> = this.modulesSubject.asObservable();

  /** Indicates whether my-modules has finished loading at least once (even if empty). */
  private modulesLoadedSubject = new BehaviorSubject<boolean>(false);
  modulesLoaded$ = this.modulesLoadedSubject.asObservable();

  /** Internal flag to avoid firing multiple parallel HTTP calls. */
  private loading = false;

  constructor(private moduleService: ModulesService) {}

  /**
   * Sets current modules and marks them as loaded.
   * This is also used by PermissionService when it already has the modules response.
   */
  setModules(modules: Module[]): void {
    this.loading = false;
    this.modulesLoadedSubject.next(true);
    this.modulesSubject.next(modules ?? []);
  }

  /**
   * Ensures that my-modules is loaded once per session.
   * - If already loaded, returns the current value and completes.
   * - If not yet loaded, triggers HTTP call (unless already in-flight) and waits until data arrives.
   */
  ensureModulesLoaded(): Observable<Module[]> {
    // If another caller already loaded modules, just return the current snapshot.
    if (this.modulesLoadedSubject.getValue()) {
      return this.modules$.pipe(take(1));
    }

    // Trigger HTTP load only once even if multiple callers arrive at the same time.
    if (!this.loading) {
      this.loading = true;
      this.moduleService.myModules().subscribe({
        next: (res) => {
          this.setModules((res?.data as Module[]) ?? []);
        },
        error: () => {
          // On error we still mark as loaded with an empty list so guards can proceed deterministically.
          this.setModules([]);
        }
      });
    }

    // Wait until modulesLoaded toggles to true, then emit the latest modules list.
    return this.modules$.pipe(
      filter(() => this.modulesLoadedSubject.getValue()),
      take(1)
    );
  }

  /**
   * Backwards-compatible fire-and-forget loader.
   * Existing code that doesn't need the modules value can keep calling this.
   */
  loadModules(): void {
    this.ensureModulesLoaded().subscribe();
  }

  /**
   * Force reload my-modules from API (e.g. after subscription renew/upgrade).
   * Updates cached modules and notifies subscribers (sidebar, breadcrumb, guards).
   */
  reloadModules(): Observable<Module[]> {
    return this.moduleService.myModules().pipe(
      map((res) => (res?.data as Module[]) ?? []),
      tap((modules) => this.setModules(modules)),
      catchError(() => {
        this.setModules([]);
        return of([]);
      })
    );
  }

  /**
   * Resolves current URL to breadcrumb and page title from my-modules (same source as sidebar).
   * Uses module name + page title from the API, and matches the route using both SIDEBAR_PAGE_MAPPING
   * and backend page.path (longest match wins).
   */
  getBreadcrumbFromCurrentRoute(url: string, isAr: boolean): BreadcrumbFromRouteResult {
    const raw = (url ?? '').split(/[?#]/)[0];
    const path = raw.replace(/^\/+|\/+$/g, '') || '';
    const pathWithLeadingSlash = path ? '/' + path : '/';
    const pathNormTrim = pathWithLeadingSlash.replace(/\/+$/, '');

    if (
      pathNormTrim === '/settings/smart-wizards' ||
      pathNormTrim.startsWith('/settings/smart-wizards/')
    ) {
      return {
        title: 'SMART_WIZARD_MANAGER.TITLE',
        breadcrumb: [
          { label: 'SIDEMENU.HOME', link: '/' },
          { label: 'SIDEMENU.SETTINGS', link: '/settings/branch' },
          { label: 'SMART_WIZARD_MANAGER.TITLE', link: '' },
        ],
      };
    }

    const normalizePath = (p: string): string => {
      if (!p?.trim()) return '';
      const withSlash = p.startsWith('/') ? p : '/' + p;
      return withSlash.replace(/\/+$/, '') || '/';
    };

    const pathNorm = pathWithLeadingSlash.replace(/\/+$/, '');
    const pathNormLower = pathNorm.toLowerCase();

    const modules = (this.modulesSubject.getValue() ?? []).filter(m => m.isActive);
    const sortedModules = [...modules].sort((a, b) => a.displayOrder - b.displayOrder);

    let best: { score: number; mod: Module; page: Page; linkPath: string } | null = null;

    for (const mod of sortedModules) {
      const pages = (mod.pages ?? []).filter(p => p.isVisible).sort((a, b) => a.displayOrder - b.displayOrder);
      for (const page of pages) {
        const mapping = SIDEBAR_PAGE_MAPPING[page.resourceName];
        const pathSet = new Set<string>();
        if (mapping?.path) pathSet.add(normalizePath(mapping.path));
        if (page.path) pathSet.add(normalizePath(page.path));
        if (mapping?.breadcrumbPaths?.length) {
          for (const bp of mapping.breadcrumbPaths) {
            if (bp) pathSet.add(normalizePath(bp));
          }
        }

        for (const mappingNorm of pathSet) {
          if (!mappingNorm || mappingNorm === '/') continue;
          const mappingNormLower = mappingNorm.toLowerCase();
          const matches =
            pathNormLower === mappingNormLower || pathNormLower.startsWith(mappingNormLower + '/');
          if (!matches) continue;
          const score = mappingNorm.length;
          if (!best || score > best.score) {
            const primary =
              mapping?.path && normalizePath(mapping.path) ? normalizePath(mapping.path) : mappingNorm;
            best = { score, mod, page, linkPath: primary };
          }
        }
      }
    }

    if (best) {
      const moduleLabel = isAr ? (best.mod.nameAr || best.mod.nameEn) : (best.mod.nameEn || best.mod.nameAr);
      const pageLabel = isAr ? (best.page.titleAr || best.page.titleEn) : (best.page.titleEn || best.page.titleAr);
      const moduleTrim = (moduleLabel ?? '').trim();
      const pageTrim = (pageLabel ?? '').trim();
      // When my-modules sends the same title for module and page (typical for dashboard/home),
      // HOME + module + page would render the same label three times after i18n (e.g. الرئيسية).
      if (moduleTrim && moduleTrim === pageTrim) {
        return {
          title: pageLabel,
          breadcrumb: [{ label: 'SIDEMENU.HOME', link: pathNormTrim || '/' }],
        };
      }
      const breadcrumb: BreadcrumbItem[] = [
        { label: 'SIDEMENU.HOME', link: '/' },
        { label: moduleLabel, link: best.linkPath || '/' },
        { label: pageLabel, link: '' },
      ];
      return { title: pageLabel, breadcrumb };
    }

    return {
      title: 'MENU.DASHBOARD',
      breadcrumb: [{ label: 'SIDEMENU.HOME', link: '/' }],
    };
  }

  /**
   * Returns View permission keys for pages mapped to a route segment or full route path.
   * Examples:
   * - 'sales'
   * - 'sales-orders'
   * - '/sales/sales-orders'
   * - '/sales/sales-orders/create-sales-order/create/new'
   */
  getRequiredPermissionKeysForPath(pathOrSegment: string): string[] {
    return this.getPermissionKeysForPathAndAction(pathOrSegment, 'View');
  }
}
